// src/ui/TaskBar.ts
import Phaser from 'phaser';
import { COLORS, DEPTHS, GAME_WIDTH, TASKBAR } from '../config/gameConfig';
import type { BadgePayload, EventBus, WindowId } from '../systems/EventBus';
import { TaskBarTile } from './TaskBarTile';
import type { WindowManager } from '../systems/WindowManager';

export class TaskBar extends Phaser.GameObjects.Container {
  private readonly bus: EventBus;
  private readonly manager: WindowManager;
  private readonly tiles = new Map<WindowId, TaskBarTile>();
  private readonly tileOrder: WindowId[] = [];

  private readonly onMinimized = (p: { windowId: WindowId }): void => {
    const tile = this.tiles.get(p.windowId);
    if (!tile) return;
    tile.setMinimized(true);
    // Re-apply badge so pulse gating recomputes against new minimized state.
    tile.applyBadge(this.manager.getBadge(p.windowId));
  };

  private readonly onRestored = (p: { windowId: WindowId }): void => {
    const tile = this.tiles.get(p.windowId);
    if (!tile) return;
    tile.setMinimized(false);
    tile.applyBadge(this.manager.getBadge(p.windowId));
  };

  private readonly onBadgeChanged = (p: { windowId: WindowId; badge: BadgePayload }): void => {
    const tile = this.tiles.get(p.windowId);
    if (!tile) return;
    tile.applyBadge(p.badge);
  };

  constructor(scene: Phaser.Scene, bus: EventBus, manager: WindowManager) {
    super(scene, 0, TASKBAR.y);
    scene.add.existing(this);
    this.setDepth(DEPTHS.taskbar);
    this.bus = bus;
    this.manager = manager;

    const bg = scene.add.rectangle(0, 0, GAME_WIDTH, TASKBAR.height, COLORS.taskbarBg).setOrigin(0, 0);
    this.add(bg);
    const border = scene.add.rectangle(0, 0, GAME_WIDTH, 1, COLORS.taskbarBorder).setOrigin(0, 0);
    this.add(border);

    // Build a tile for every registered window up-front. They start in "open"
    // (non-minimized) state and stay visible for the entire session.
    for (const windowId of this.manager.getRegistered()) {
      const badge = this.manager.getBadge(windowId);
      const tile = new TaskBarTile(scene, bus, windowId, badge);
      tile.setMinimized(false);
      this.add(tile);
      this.tiles.set(windowId, tile);
      this.tileOrder.push(windowId);
    }
    this.layoutTiles(false);

    this.bus.on('window:minimized', this.onMinimized);
    this.bus.on('window:restored', this.onRestored);
    this.bus.on('window:badge-changed', this.onBadgeChanged);

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  private layoutTiles(animate: boolean): void {
    const tileY = (TASKBAR.height - TASKBAR.tileHeight) / 2;
    this.tileOrder.forEach((windowId, index) => {
      const tile = this.tiles.get(windowId);
      if (!tile) return;
      const targetX = TASKBAR.marginX + index * (TASKBAR.tileWidth + TASKBAR.tileGap);
      if (animate) {
        this.scene.tweens.killTweensOf(tile);
        this.scene.tweens.add({
          targets: tile,
          x: targetX,
          y: tileY,
          duration: TASKBAR.tileLayoutTweenMs,
          ease: 'Sine.easeOut',
        });
      } else {
        tile.setPosition(targetX, tileY);
      }
    });
  }

  private teardown(): void {
    this.bus.off('window:minimized', this.onMinimized);
    this.bus.off('window:restored', this.onRestored);
    this.bus.off('window:badge-changed', this.onBadgeChanged);
  }
}
